// src/common/components/ratingModal/RatingModal.tsx
import React from "react";
import { createPortal } from "react-dom";
import { MdClose, MdStar, MdStarOutline } from "react-icons/md";
import { useTheme } from "../../hooks/useTheme";
import { useRatingModal } from "./useRatingModal";
import styles from "./RatingModal.module.scss";

export default function RatingModal() {
  const { theme } = useTheme();
  const {
    isOpen,
    itemName,
    itemImage,
    rating,
    isLoading,
    isRatingSelected,
    setRating,
    handleClose,
    handleSubmit,
  } = useRatingModal();

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div
        className={styles.modal}
        style={{ backgroundColor: theme.background }}
      >
        <div
          className={styles.header}
          style={{ borderBottomColor: theme.border }}
        >
          <button
            className={styles.closeButton}
            onClick={handleClose}
            disabled={isLoading}
          >
            <MdClose size={24} color={theme.text} />
          </button>
          <div className={styles.titleContainer}>
            <h2 className={styles.title} style={{ color: theme.text }}>
              Оценить товар
            </h2>
            <p
              className={styles.subtitle}
              style={{ color: theme.textSecondary }}
            >
              Поделитесь вашим мнением
            </p>
          </div>
        </div>

        <div className={styles.content}>
          <div
            className={styles.section}
            style={{
              backgroundColor: theme.card,
              borderColor: theme.border,
            }}
          >
            <div
              className={styles.itemCard}
              style={{ backgroundColor: theme.background }}
            >
              {itemImage && (
                <img
                  src={itemImage}
                  alt={itemName}
                  className={styles.itemImage}
                />
              )}
              <div className={styles.itemInfo}>
                <h3 className={styles.itemName} style={{ color: theme.text }}>
                  {itemName}
                </h3>
                <p
                  className={styles.itemHint}
                  style={{ color: theme.textSecondary }}
                >
                  Покупка подтверждена
                </p>
              </div>
            </div>
          </div>

          <div
            className={styles.section}
            style={{
              backgroundColor: theme.card,
              borderColor: theme.border,
            }}
          >
            <h3 className={styles.ratingTitle} style={{ color: theme.text }}>
              Как вам товар?
            </h3>
            <div className={styles.starsContainer}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  className={styles.starButton}
                  onClick={() => setRating(star)}
                  disabled={isLoading}
                >
                  {star <= rating ? (
                    <MdStar size={40} color="#FFD700" />
                  ) : (
                    <MdStarOutline size={40} color={theme.textSecondary} />
                  )}
                </button>
              ))}
            </div>
            <p
              className={styles.ratingValue}
              style={{
                color: rating === 0 ? theme.danger : theme.textSecondary,
              }}
            >
              {rating === 0 ? "Выберите оценку" : `${rating} из 5`}
            </p>
          </div>

          <div
            className={styles.section}
            style={{
              backgroundColor: theme.card,
              borderColor: theme.border,
            }}
          >
            <div className={styles.footer}>
              <button
                className={styles.cancelButton}
                onClick={handleClose}
                disabled={isLoading}
                style={{
                  backgroundColor: theme.card,
                  borderColor: theme.border,
                  color: theme.text,
                }}
              >
                Отмена
              </button>

              <button
                className={`${styles.submitButton} ${!isRatingSelected || isLoading ? styles.disabled : ""}`}
                onClick={handleSubmit}
                disabled={isLoading || !isRatingSelected}
                style={{ backgroundColor: theme.primary }}
              >
                <span className={styles.submitButtonText}>
                  {isLoading ? "Отправка..." : "Отправить"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
